import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Unlock, Clock, Ban, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEVEN_DAYS = 7 * 86400 * 1000;

const statusOf = (u) => {
  const g = u.admin_access;
  if (u.is_admin) return { label: 'Admin', cls: 'bg-purple-100 text-purple-700' };
  if (g && !g.expired) return { label: `Liberado · ${g.days_left}d restantes`, cls: 'bg-green-100 text-green-700' };
  if (g && g.expired) return { label: 'Liberação expirada', cls: 'bg-red-100 text-red-700' };
  if (u.payment_status === 'paid' && u.subscription_plan !== 'free_trial') return { label: `Pago · ${u.subscription_plan}`, cls: 'bg-blue-100 text-blue-700' };
  if (u.payment_status === 'trial_expired' || u.trial_expired) return { label: 'Trial expirado', cls: 'bg-red-100 text-red-700' };
  if (u.subscription_plan === 'free_trial') return { label: `Trial · ${u.trial_days_left}d`, cls: 'bg-amber-100 text-amber-700' };
  return { label: 'Aguardando pagamento', cls: 'bg-gray-100 text-gray-700' };
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const AdminAccessPanel = ({ users, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);

  const token = localStorage.getItem('token');
  const cfg = { headers: { Authorization: `Bearer ${token}` } };

  const isNew = (u) => u.created_at && Date.now() - new Date(u.created_at).getTime() < SEVEN_DAYS;
  const candidates = users.filter((u) => !u.is_admin);
  const newCount = candidates.filter(isNew).length;
  const activeGrants = candidates.filter((u) => u.admin_access && !u.admin_access.expired).length;

  const visible = candidates
    .filter((u) => !onlyNew || isNew(u))
    .filter((u) => !search || `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 50);

  const grant = async (u, days) => {
    if (!window.confirm(`Liberar o sistema completo (limites Pro) por ${days} dias para ${u.email}?`)) return;
    setBusy(`${u.id}-${days}`);
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/grant-access`, { days }, cfg);
      toast.success(data.message);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao liberar acesso');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Encerrar agora a liberação de ${u.email}? Ele verá a tela de pagamento.`)) return;
    setBusy(`${u.id}-revoke`);
    try {
      await axios.post(`${API}/admin/users/${u.id}/revoke-access`, {}, cfg);
      toast.success('Liberação encerrada');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao encerrar liberação');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-8" data-testid="admin-access-panel">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <UserPlus className="h-5 w-5 mr-2 text-emerald-600" />
          Cadastros e Liberação de Acesso
          <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="new-signups-badge">
            {newCount} novo{newCount === 1 ? '' : 's'} em 7 dias
          </span>
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" data-testid="active-grants-badge">
            {activeGrants} liberado{activeGrants === 1 ? '' : 's'}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} data-testid="only-new-toggle" />
            Só novos (7 dias)
          </label>
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-2 top-2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nome, email, telefone"
              className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded w-56"
              data-testid="access-search-input"
            />
          </div>
        </div>
      </div>

      <p className="px-6 pt-3 text-xs text-gray-500">
        Quem se cadastra aparece aqui. Clique em <strong>10 dias</strong> ou <strong>30 dias</strong> para liberar o sistema com limites Pro.
        Ao terminar o prazo o sistema trava e mostra a tela de pagamento; você pode liberar mais dias quando quiser.
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-6 py-2 text-left">Usuário</th>
              <th className="px-4 py-2 text-left">Cadastro</th>
              <th className="px-4 py-2 text-left">Situação</th>
              <th className="px-4 py-2 text-left">Liberado até</th>
              <th className="px-4 py-2 text-right">Liberar acesso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="access-users-table">
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-6 text-center text-gray-500">Nenhum cadastro encontrado</td></tr>
            )}
            {visible.map((u) => {
              const st = statusOf(u);
              const g = u.admin_access;
              return (
                <tr key={u.id} className={isNew(u) ? 'bg-emerald-50/40' : ''} data-testid={`access-row-${u.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {u.name || '—'}
                      {isNew(u) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold">NOVO</span>}
                    </div>
                    <div className="text-xs text-gray-500">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`} data-testid={`access-status-${u.id}`}>{st.label}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {g ? (
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(g.end)}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => grant(u, 10)}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid={`grant-10-${u.id}`}
                      >
                        <Unlock className="h-3 w-3" /> {busy === `${u.id}-10` ? '...' : '10 dias'}
                      </button>
                      <button
                        onClick={() => grant(u, 30)}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid={`grant-30-${u.id}`}
                      >
                        <Unlock className="h-3 w-3" /> {busy === `${u.id}-30` ? '...' : '30 dias'}
                      </button>
                      {g && !g.expired && (
                        <button
                          onClick={() => revoke(u)}
                          disabled={!!busy}
                          title="Encerrar liberação agora"
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 text-xs disabled:opacity-50"
                          data-testid={`revoke-${u.id}`}
                        >
                          <Ban className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAccessPanel;
